import React, { FC, useMemo } from 'react';
import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  createHttpLink,
  FetchResult,
  InMemoryCache,
  InMemoryCacheConfig,
  NextLink,
  Operation,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { ErrorResponse, onError } from '@apollo/client/link/error';
import { fromPromise } from '@apollo/client/link/utils';
import { Observable } from '@apollo/client/utilities';
import type { OktaAuth } from '@okta/okta-auth-js';
import { useOktaAuth } from '@okta/okta-react';
import { createSplunkErrorLink, errorLink, logAuthTokenRefreshFailure, useSplunkLogger } from '@resolutionlife/ui';
import { v4 as uuidv4 } from 'uuid';

import { createDPoPProof } from '../auth/dpop-utils';
import { fileActivityEventsPagination } from '../components/file-activity-list/cache';
import env from '../configs/env';

export const apolloCacheOptions: InMemoryCacheConfig = {
  possibleTypes: {
    StorageObject: ['File', 'Directory'],
  },
  typePolicies: {
    CedantDetails: {
      fields: {
        ...fileActivityEventsPagination,
      },
    },
  },
};

// Generate transaction ID once per session
let transactionId: string | null = null;
const getTransactionId = () => {
  if (!transactionId) {
    transactionId = uuidv4();
  }
  return transactionId;
};

// Generate telemetry headers
const generateTraceId = () => uuidv4().replace(/-/g, '');
const generateSpanId = () => uuidv4().replace(/-/g, '').substring(0, 16);
const generateTraceparent = () => {
  const version = '00';
  const traceId = generateTraceId();
  const spanId = generateSpanId();
  const traceFlags = '01'; // sampled
  return `${version}-${traceId}-${spanId}-${traceFlags}`;
};

// Extract tenant ID from ID token
const extractTenantId = (idToken: string): string => {
  try {
    const parsedIdToken = JSON.parse(atob(idToken.split('.')[1]));
    if (parsedIdToken?.iss) {
      const issuerUrl = new URL(parsedIdToken.iss);
      return issuerUrl.hostname.split('.')[0];
    }
    if (parsedIdToken?.aud) {
      return parsedIdToken.aud;
    }
  } catch (e) {
    // Token parsing failed, use default
  }
  return 'default-tenant';
};

// Generate DPoP proof with error handling
export const generateDPoPProof = async (uri: string, accessToken: string): Promise<string | undefined> => {
  try {
    return await createDPoPProof('POST', uri, accessToken);
  } catch (e) {
    // DPoP generation failed, continue without it
    return undefined;
  }
};

// Add retry logic for 401 errors during token renewal
const createRetryDelay = () => new Promise<void>((resolve) => {
  setTimeout(() => resolve(), 1000);
});

const executeRetry = (operation: Operation, forward: NextLink): Observable<FetchResult> => {
  return fromPromise(createRetryDelay()).flatMap(() => forward(operation));
};

// Create auth headers
const createAuthHeaders = async (oktaAuth: OktaAuth, headers: any, uri: string, accessToken: string, idToken: string | undefined) => {
  const tenantId = idToken ? extractTenantId(idToken) : 'default-tenant';
  const tokens = await oktaAuth.tokenManager.getTokens();
  // rely on okta sdk to generate valid dpop headers (so that the same public + private key pair has been used throughout process)
  const authHeaders = await oktaAuth.getDPoPAuthorizationHeaders({ url: uri, method: "POST", accessToken: tokens?.accessToken });
  return {
    ...headers,
    ...authHeaders,
    'x-scope-tenant': tenantId,
    'x-correlation-id': uuidv4(),
    'x-transaction-id': getTransactionId(),
    'traceparent': generateTraceparent(),
  };
};

export const GraphQLProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const oktaAuthResult = useOktaAuth();
  const oktaAuth = oktaAuthResult?.oktaAuth;
  const splunkLogger = useSplunkLogger();

  const client = useMemo(() => {
    const httpLink = createHttpLink({
      uri: env.api.host + env.api.paths.graphql,
    });

    const authLink = setContext(async (_, { headers }) => {
      const accessToken = oktaAuth?.getAccessToken();
      
      if (!accessToken) {
        return { headers };
      }
      
      const idToken = oktaAuth?.getIdToken();
      const authHeaders = await createAuthHeaders(
        oktaAuth,
        headers,
        env.api.host + env.api.paths.graphql,
        accessToken,
        idToken
      );

      return { headers: authHeaders };
    });

    const handleRetryError = (error: ErrorResponse): Observable<FetchResult> | void => {
      const { networkError, operation, forward } = error;
      const isUnauthorized = networkError && 'statusCode' in networkError && networkError.statusCode === 401;
      if (isUnauthorized) {
        logAuthTokenRefreshFailure(splunkLogger, {
          provider: 'Okta',
          reason: `401 Unauthorized on operation ${operation.operationName || 'unknown'}`,
        });
        return executeRetry(operation, forward);
      }
    };

    const retryLink = onError(handleRetryError);

    const splunkErrorLink = createSplunkErrorLink(splunkLogger);
    const links = [splunkErrorLink, errorLink, retryLink, authLink, httpLink];

    return new ApolloClient({
      link: ApolloLink.from(links),
      cache: new InMemoryCache(apolloCacheOptions),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'cache-first',
          errorPolicy: 'all', // Show partial data even with errors
        },
        query: {
          errorPolicy: 'all',
        },
      },
    });
  }, [oktaAuth, splunkLogger]);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
